import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatTabsModule } from "@angular/material/tabs";
import { Subscription } from "rxjs";

import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  BscEjercicioKpi,
  BscEjercicioTrimestre,
  BscHistorial,
  BscKpiFicha,
  BscKpiMes,
  BscKpiTrimestre,
  BscRes,
  CorteBsc,
  EstadoMesBsc,
  EstatusTrimestreBsc,
  SemaforoBsc,
  ViaCapturaBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";
import {
  ACCION_HISTORIAL,
  ESTADO_MES,
  ESTATUS_PLANEACION,
  ESTATUS_TRIMESTRE,
  EstadoBsc,
  SEMAFORO,
  textoSinAnterior,
} from "../bsc-estados";
import {
  SIN_DATO,
  fechaCorta,
  fechaHora,
  meta,
  mesEnTexto,
  mesesDelTrimestre,
  nombreMes,
  numero,
  pct,
  pts,
  trimestreDeMes,
  unidad,
} from "../bsc-formato";
import { acumProgEn, acumRealEn, aplicaEn, pesoEn, ponderadoEn, semaforoEn } from "../bsc-kpi";
import { AcumuladoQBscComponent } from "../graficas/acumulado-q-bsc.component";
import { CapturaMesBscComponent, ResultadoCapturaMesBsc, abrirCapturaMesBsc } from "./captura-mes-bsc.component";

/** Una cifra del trimestre ("Avance real 90%"). */
interface CifraQ {
  etiqueta: string;
  valor: string;
  /** Debajo del valor: la letra del FOR-011 o lo que la explica. */
  ayuda: string;
}

/** Una tarjeta de mes: actividad planeada y evidencia, con quién capturó. */
interface TarjetaMes {
  mes: number;
  nombre: string;
  estado: EstadoBsc<EstadoMesBsc> | null;
  actividad: string;
  evidencia: string;
  /** "Capturó Ana López · 5 may 2026". */
  capturo: string;
  /** Lo que se dice cuando el mes no trae texto. */
  vacio: string;
  /** F6.4: se abre el cuadro de captura (ver armaCaptura). */
  capturable: boolean;
}

/** Una pestaña de trimestre, armada UNA vez por respuesta. */
interface PestanaQ {
  q: number;
  /** "Q1 · Autorizado · 25%". */
  etiqueta: string;
  /** is-verde | is-amarillo | is-rojo | is-neutro: el punto del semáforo. */
  punto: string;
  /** El semáforo en palabras para lectores de pantalla ("en meta"); vacío sin semáforo. */
  puntoTexto: string;
  /** "Q2 2026 · abril a junio". */
  titulo: string;
  estatus: EstadoBsc<EstatusTrimestreBsc> | null;
  semaforo: EstadoBsc<SemaforoBsc> | null;
  /** NA, sin avance o sin planeación autorizada. */
  nota: string;
  cifras: CifraQ[];
  meses: TarjetaMes[];
  /** Recursos, comentario, quién envió y resolvió, fecha límite. */
  pie: { etiqueta: string; valor: string }[];
}

/** Una cifra del tile «Mismo KPI en <año anterior>» (F7.3): "2025 · Q2 44%". */
interface CifraContra {
  etiqueta: string;
  valor: string;
  /** La diferencia en el tono de su signo: is-exito | is-peligro. */
  clase: "" | "is-exito" | "is-peligro";
}

/** Un peso del trimestre en la tarjeta lateral. */
interface PesoQ {
  q: number;
  valor: string;
  na: boolean;
}

/** Un renglón de la bitácora del KPI. */
interface FilaHistorial {
  fecha: string;
  accion: string;
  detalle: string[];
  usuario: string;
  /** CAMBIO_META / CAMBIO_PESOS de otra planeación del mismo KPI maestro (§10). */
  otroAnio: boolean;
}

/** Un renglón del pie del trimestre: "Envió", "Autorizó", "Fecha límite"… */
interface RenglonPie {
  etiqueta: string;
  valor: string;
}

/** Un valor de los parámetros de una ruta (?anio=&corte=…). */
type ValorQuery = string | number | null;

/** Los cortes de A2, para volver al mismo. */
const CORTES = new Set<CorteBsc>(["1", "2", "3", "4", "ANUAL"]);
const TRIMESTRES = [1, 2, 3, 4];

const RUTA = {
  tablero: "/admin/bsc/tablero",
  area: "/admin/bsc/tablero/area",
  comparativo: "/admin/bsc/comparativo",
};

/**
 * BSC · A3 Ficha de KPI (docs/BSC.md §7 y Anexo B, maqueta «A3-FichaKPI»):
 * un KPI del año. Una pestaña por trimestre con su estatus (avance real,
 * programado, peso, ponderado y acumulado; las 3 tarjetas de mes con
 * actividad planeada, evidencia y quién capturó; recursos; quién envió y
 * quién autorizó) y al lado el acumulado programado contra real, los pesos
 * por trimestre, la descripción y los beneficios, el mismo KPI el año
 * anterior y la bitácora del KPI (con los cambios de meta de otros años).
 *
 * La lectura (F4) pide la hoja raíz admin/bsc y que el KPI sea de la
 * empresa (kpi/ficha.do, BscConsultaBuss). No hay "Editar meta" (las metas
 * quedan bloqueadas al autorizar la planeación, Anexo B). "Capturar <mes>"
 * (F6.4) abre el cuadro captura-mes-bsc de C1 solo si quien consulta captura
 * en el área (puedeCapturar de la ficha: hoja mi-bsc y el área en sus
 * manos), la vía del área admite el flujo guiado (la de catalogos.do, que
 * también dice si el año está cerrado), la planeación está AUTORIZADA y no es
 * de un FOR-011 histórico, el KPI sigue ACTIVO y el trimestre del mes no está
 * ENVIADO ni AUTORIZADO. El botón del hero toma el primer mes ya empezado sin
 * captura en que el KPI aplica; cada tarjeta de mes capturable trae el suyo.
 *
 * La ficha llega sin recortar (sus 4 trimestres son como el Excel: L se
 * arrastra en un trimestre sin captura); para no pintar un semáforo ni un
 * acumulado que nadie capturó, un trimestre sin avance real se enseña "sin
 * avance" y la gráfica solo pinta lo real hasta el último trimestre con
 * avance.
 *
 * Base visual: admin/ajustes (.ajustes-hero, .ajustes-card,
 * .ajustes-layout) con mat-tab-group por trimestre (los temas lo pintan en
 * tema-formularios.css). Llega desde A2 con ?corte=&q= para abrir la pestaña
 * del corte y regresar al mismo.
 */
@Component({
  selector: "app-ficha-kpi-bsc",
  standalone: true,
  templateUrl: "./ficha-kpi-bsc.component.html",
  styleUrls: ["../bsc.shared.css", "./ficha-kpi-bsc.component.css"],
  imports: [CommonModule, RouterLink, MatTabsModule, AcumuladoQBscComponent],
})
export class FichaKpiBscComponent implements OnInit, OnDestroy {
  readonly ruta = RUTA;
  @ViewChild("historialTitulo") private readonly historialTitulo?: ElementRef<HTMLElement>;

  idEjercicioKpi: number | null = null;
  /** El corte de A2, para volver a él. */
  corte: CorteBsc | null = null;
  /** La pestaña que pidió A2 (el trimestre del corte). */
  private qPedido: number | null = null;

  loading = false;
  error = "";

  ficha: BscKpiFicha | null = null;
  kpi: BscEjercicioKpi | null = null;
  anterior: BscEjercicioKpi | null = null;
  pestanas: PestanaQ[] = [];
  indice = 0;
  serieReal: (number | null)[] = [];
  serieProgramado: (number | null)[] = [];
  pesos: PesoQ[] = [];
  textoSumaPesos = "";
  sumaPesosAviso = false;
  historial: FilaHistorial[] = [];
  avisos: string[] = [];
  /*
   * F7.3, el tile «Mismo KPI en <año anterior>» (armaAnterior): los dos años
   * al mismo trimestre y su diferencia, con los números del servidor.
   */
  contraCifras: CifraContra[] = [];
  /** Debajo de las cifras: hacia dónde va este año, o por qué no se compara. */
  contraNota = "";
  contraNotaClase: "" | "is-exito" | "is-peligro" = "";
  /** La meta del año anterior (si cambió, con su motivo) y su cierre. */
  anteriorTexto: string[] = [];
  /** «Comparar los dos años» (T2): el año anterior tiene planeación del área. */
  contraEnlace = false;

  puedeComparar = false;

  /* ---------- Captura (F6.4) ---------- */
  /** puedeCapturar de la ficha: hoja mi-bsc y el área en mis manos (BscAccesos.banderas). */
  puedeCapturar = false;
  /** El mes del botón «Capturar <mes>» del hero; nulo si no hay qué capturar. */
  mesCaptura: number | null = null;
  /** Lo que se guardó en el cuadro (región role="status"). */
  mensajeCaptura = "";
  private mesesFicha: BscKpiMes[] = [];
  /** La vía del área y si el año está cerrado, de catalogos.do; nulos mientras no se leen. */
  private viaArea: ViaCapturaBsc | null = null;
  private cicloCerrado: boolean | null = null;
  /** catalogos.do, para la vía de cada área y el estatus de cada año. */
  private catalogos: BscRes | null = null;
  private catalogo$?: Subscription;
  private dialogo?: MatDialogRef<CapturaMesBscComponent, ResultadoCapturaMesBsc>;

  private consulta?: Subscription;
  private ruta$?: Subscription;

  constructor(
    private readonly bscService: BscService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.puedeComparar = tieneAccesoMenu(this.authService.getMenuList(), RUTA.comparativo);
    // Angular reutiliza el componente si solo cambia el KPI de la ruta (la barra
    // de direcciones o el historial del navegador): se vuelve a consultar.
    this.ruta$ = this.route.paramMap.subscribe((ruta) => {
      const id = Number(ruta.get("idEjercicioKpi"));
      this.idEjercicioKpi = Number.isInteger(id) && id > 0 ? id : null;
      const params = this.route.snapshot.queryParamMap;
      const corte = (params.get("corte") ?? "").toUpperCase() as CorteBsc;
      this.corte = CORTES.has(corte) ? corte : null;
      const q = Number(params.get("q"));
      this.qPedido = Number.isInteger(q) && q >= 1 && q <= 4 ? q : null;
      if (this.idEjercicioKpi == null) {
        this.consulta?.unsubscribe();
        this.loading = false;
        this.limpia();
        this.error = "El enlace no trae un KPI válido. Ábrelo desde el detalle del área.";
        this.cdr.detectChanges();
        return;
      }
      this.consultar();
    });
  }

  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.ruta$?.unsubscribe();
    this.catalogo$?.unsubscribe();
    // El cuadro vive en el overlay y sobrevive al cambio de ruta.
    this.dialogo?.close();
  }

  /* ============================ Consulta ============================ */

  /**
   * @param silencioso tras guardar en el cuadro de captura: la ficha se queda
   *        a la vista (y en la misma pestaña) mientras contesta.
   */
  consultar(silencioso = false): void {
    if (this.idEjercicioKpi == null) {
      return;
    }
    this.consulta?.unsubscribe();
    if (!silencioso) {
      // Lo del KPI anterior no se queda en el encabezado mientras contesta (ni si falla).
      this.limpia();
      this.loading = true;
      this.mensajeCaptura = "";
    }
    this.error = "";
    this.cdr.detectChanges();
    const pedido = this.idEjercicioKpi;
    this.consulta = this.bscService.kpiFicha(pedido, this.corteFicha).subscribe({
      next: (res) => {
        if (pedido !== this.idEjercicioKpi) {
          return;
        }
        this.loading = false;
        if (res?.codigoError) {
          this.error = res.codigoError === "AUTH-001"
            ? "Tu sesión caducó. Vuelve a entrar."
            : res.mensajeError || "No se pudo consultar la ficha del KPI.";
          this.cdr.detectChanges();
          return;
        }
        this.aplica(res, silencioso);
        this.cdr.detectChanges();
      },
      error: () => {
        if (pedido !== this.idEjercicioKpi) {
          return;
        }
        this.loading = false;
        this.error = "No se pudo consultar la ficha del KPI: sin conexión con el servidor.";
        this.cdr.detectChanges();
      },
    });
  }

  private limpia(): void {
    this.ficha = null;
    this.kpi = null;
    this.mesesFicha = [];
    this.puedeCapturar = false;
    this.mesCaptura = null;
    this.anterior = null;
    this.pestanas = [];
    this.historial = [];
    this.avisos = [];
    this.contraCifras = [];
    this.contraNota = "";
    this.contraNotaClase = "";
    this.anteriorTexto = [];
    this.contraEnlace = false;
  }

  private aplica(res: BscRes, silencioso = false): void {
    const f: BscKpiFicha = res.kpiFicha ?? {};
    this.ficha = f;
    this.kpi = f.kpi ?? null;
    this.anterior = res.kpiAnterior ?? null;
    this.avisos = res.avisos ?? [];
    const autorizada = f.estatusPlaneacion === "AUTORIZADA";
    const meses = res.meses ?? [];
    this.mesesFicha = meses;
    this.puedeCapturar = res.puedeCapturar === true;

    this.pestanas = this.kpi
      ? TRIMESTRES.map((q) => this.pestana(f, this.kpi as BscEjercicioKpi, q, autorizada, meses))
      : [];
    if (!silencioso || this.indice < 0 || this.indice >= this.pestanas.length) {
      this.indice = this.pestanaInicial(f) - 1;
    }
    this.armaSeries(f, autorizada);
    this.armaPesos(f);
    this.historial = (res.historial ?? []).slice().reverse().map((h) => this.filaHistorial(h, f));
    this.armaAnterior(f, res.historial ?? []);
    this.armaCaptura();
    this.pideCatalogos();
  }

  /* ============================ Captura (F6.4) ============================ */

  /**
   * La vía del área y el estatus del año salen de catalogos.do (la ficha no
   * los trae). Se piden una vez y solo si la ficha ya dice que capturo; un
   * área INACTIVA no está en el catálogo y su KPI se queda sin captura aquí
   * (se captura desde Mi BSC, si la ofrece).
   */
  private pideCatalogos(): void {
    if (!this.puedeCapturar || this.viaArea != null || this.catalogo$ || this.ficha?.estatusPlaneacion !== "AUTORIZADA") {
      return;
    }
    this.catalogo$ = this.bscService.catalogos().subscribe({
      next: (res) => {
        if (res?.codigoError) {
          return;
        }
        this.catalogos = res;
        this.armaCaptura();
        this.cdr.detectChanges();
      },
      error: () => {
        // Sin catálogos no se ofrece capturar: la ficha sigue siendo de lectura.
      },
    });
  }

  /**
   * Marca las tarjetas de mes que se capturan y elige el mes del botón del
   * hero. La misma regla que C1 (Mi BSC); el servidor la vuelve a exigir en
   * mes.do y real.do.
   */
  private armaCaptura(): void {
    const f = this.ficha;
    const k = this.kpi;
    const area = (this.catalogos?.catalogos?.areas ?? []).find((a) => a.idBscArea === f?.idBscArea);
    const ciclo = (this.catalogos?.catalogos?.anios ?? []).find((c) => c.anio === f?.anio);
    this.viaArea = area ? area.viaCaptura ?? "AMBAS" : null;
    this.cicloCerrado = ciclo ? ciclo.estatus === "CERRADO" : null;
    const habilitada =
      !!f &&
      !!k &&
      this.puedeCapturar &&
      this.viaArea != null &&
      this.viaArea !== "EXCEL" &&
      this.cicloCerrado === false &&
      f.estatusPlaneacion === "AUTORIZADA" &&
      f.origenPlaneacion !== "IMPORTACION_HISTORICA" &&
      k.estatus !== "RETIRADO";
    this.mesCaptura = null;
    for (const p of this.pestanas) {
      const flujo = (f?.estatusTrimestres ?? []).find((t) => t.trimestre === p.q);
      const abierto = !!flujo && flujo.estatus !== "ENVIADO" && flujo.estatus !== "AUTORIZADO";
      for (const m of p.meses) {
        m.capturable = habilitada && abierto && m.estado?.valor !== "BLOQUEADO";
      }
    }
    if (!habilitada || !k) {
      return;
    }
    this.mesCaptura = this.primerMesPorCapturar(f, k);
  }

  /** El primer mes ya empezado, sin captura, en que el KPI aplica; nulo si no hay. */
  private primerMesPorCapturar(f: BscKpiFicha | null, k: BscEjercicioKpi): number | null {
    let mes: number | null = null;
    const hoy = new Date();
    const anio = f?.anio ?? hoy.getFullYear();
    for (const p of this.pestanas) {
      for (const m of p.meses) {
        const empezado = anio < hoy.getFullYear() || (anio === hoy.getFullYear() && m.mes <= hoy.getMonth() + 1);
        if (mes == null && m.capturable && empezado && aplicaEn(k, p.q) && !this.mesCapturado(m.mes)) {
          mes = m.mes;
        }
      }
    }
    return mes;
  }

  private mesCapturado(mes: number): boolean {
    return this.mesesFicha.find((x) => x.mes === mes)?.id != null;
  }

  /** "septiembre", para el botón. */
  textoMes(mes: number | null): string {
    return mesEnTexto(mes);
  }

  /** Abre el cuadro de captura del mes (el del hero o el de una tarjeta). */
  capturarMes(mes: number | null): void {
    const f = this.ficha;
    const k = this.kpi;
    if (mes == null || !f || !k || this.dialogo) {
      return;
    }
    const q = trimestreDeMes(mes);
    this.mensajeCaptura = "";
    this.dialogo = abrirCapturaMesBsc(this.dialog, {
      kpi: k,
      anio: f.anio ?? null,
      area: f.area ?? "",
      mes,
      mesGuardado: this.mesesFicha.find((x) => x.mes === mes) ?? null,
      avance: (f.trimestres ?? []).find((t) => t.trimestre === q) ?? null,
      estatusTrimestre: (f.estatusTrimestres ?? []).find((t) => t.trimestre === q)?.estatus ?? null,
    });
    this.dialogo.afterClosed().subscribe((r) => {
      this.dialogo = undefined;
      if (!r) {
        return;
      }
      this.mensajeCaptura = r.mensaje;
      if (r.guardado || r.recargar) {
        // La pestaña del mes capturado, para ver lo que se guardó.
        this.indice = q - 1;
        this.consultar(true);
      } else {
        this.cdr.detectChanges();
      }
    });
  }

  /* ============================ Encabezado ============================ */

  get titulo(): string {
    return this.kpi?.objetivo || "Ficha de KPI";
  }

  get anio(): number | null {
    return this.ficha?.anio ?? null;
  }

  get textoMeta(): string {
    const k = this.kpi;
    return k ? meta(k.metaValor, k.metaTexto, k.unidad) : SIN_DATO;
  }

  get textoUnidad(): string {
    return unidad(this.kpi?.unidad);
  }

  /** "Meta bloqueada · planeación autorizada 20 ene 2026" o por qué todavía no. */
  get textoBloqueo(): string {
    const f = this.ficha;
    if (!f) {
      return "";
    }
    if (f.estatusPlaneacion !== "AUTORIZADA") {
      const estatus = ESTATUS_PLANEACION.find((e) => e.valor === f.estatusPlaneacion);
      const texto = textoPlaneacion(estatus);
      return `Planeación ${texto}: la meta todavía puede cambiar`;
    }
    const fecha = fechaCorta(f.fechaAutorizacion, "");
    const elDia = fecha ? ` el ${fecha}` : "";
    if (f.origenPlaneacion === "IMPORTACION_ARRANQUE" || f.origenPlaneacion === "IMPORTACION_HISTORICA") {
      const quien = f.usuarioAutoriza ? ` por ${f.usuarioAutoriza}` : "";
      return `Meta bloqueada · importada del FOR-011${elDia}${quien}`;
    }
    return `Meta bloqueada · planeación autorizada${elDia}`;
  }

  get bloqueada(): boolean {
    return this.ficha?.estatusPlaneacion === "AUTORIZADA";
  }

  /** Los parámetros de vuelta al área (mismo año y corte). */
  get queryArea(): Record<string, ValorQuery> {
    return { anio: this.anio, corte: this.corte };
  }

  get queryTablero(): Record<string, ValorQuery> {
    return { anio: this.anio, corte: this.corte };
  }

  /**
   * El trimestre del tile del año anterior que se pide a kpi/ficha.do
   * (F7.3): el corte de A2 o, si se llegó con solo ?q= (Mi BSC, la
   * revisión), ese trimestre; nulo = el corte vigente de T2.
   */
  private get corteFicha(): CorteBsc | null {
    return this.corte ?? (this.qPedido != null ? (`${this.qPedido}` as CorteBsc) : null);
  }

  /**
   * T2 (F7.1) del área del KPI: su año contra el anterior al trimestre que
   * compara el tile (F7.3); sin él, el corte vigente de T2.
   */
  get queryComparativo(): Record<string, ValorQuery> {
    const q = this.ficha?.trimestreComparado ?? null;
    return {
      anioA: this.anio != null ? this.anio - 1 : null,
      anioB: this.anio,
      idBscArea: this.ficha?.idBscArea ?? null,
      modo: q != null ? "corte" : null,
      q,
    };
  }

  /** Lleva el foco al historial (el botón del hero). */
  verHistorial(): void {
    const titulo = this.historialTitulo?.nativeElement;
    if (titulo) {
      titulo.scrollIntoView({ behavior: "smooth", block: "start" });
      titulo.focus({ preventScroll: true });
    }
  }

  /* ============================ Trimestres ============================ */

  private pestana(
    f: BscKpiFicha,
    k: BscEjercicioKpi,
    q: number,
    autorizada: boolean,
    meses: BscKpiMes[],
  ): PestanaQ {
    const t = (f.trimestres ?? []).find((x) => x.trimestre === q);
    const flujo = (f.estatusTrimestres ?? []).find((x) => x.trimestre === q);
    const aplica = aplicaEn(k, q);
    const real = numero(t?.avanceReal);
    const conAvance = autorizada && aplica && real != null;
    const estatus = ESTATUS_TRIMESTRE.find((e) => e.valor === flujo?.estatus) ?? null;
    const semaforo = conAvance ? SEMAFORO.find((s) => s.valor === (t?.semaforo ?? semaforoEn(k, q))) ?? null : null;

    // Los trimestres después del corte de un histórico se quedan PENDIENTE sin transición (§4).
    const sinDatos = flujo?.estatus === "PENDIENTE" && f.origenPlaneacion === "IMPORTACION_HISTORICA";
    const nota = notaDelTrimestre(k, q, sinDatos, autorizada, aplica, real);

    const ponderado = conAvance ? numero(t?.ponderado ?? ponderadoEn(k, q)) : null;
    const acumulado = conAvance ? numero(t?.acumReal ?? acumRealEn(k, q)) : null;
    const programadoAcum = numero(t?.acumProg ?? acumProgEn(k, q));
    const cifras: CifraQ[] = [
      { etiqueta: "Avance real", valor: aplica ? pct(real) : "NA", ayuda: "I del FOR-011" },
      { etiqueta: "Programado", valor: aplica ? pct(t?.avanceProgramado ?? 1) : "NA", ayuda: "J del FOR-011" },
      { etiqueta: "Peso del trimestre", valor: aplica ? pct(pesoEn(k, q)) : "NA", ayuda: "M, sobre el año" },
      { etiqueta: "Ponderado", valor: pct(ponderado), ayuda: "K = I × M / J" },
      { etiqueta: "Acumulado", valor: pct(acumulado), ayuda: `L, de ${pct(programadoAcum)} (N)` },
    ];
    agregaUnidades(cifras, k, t, aplica);

    const etiquetaEstatus = sinDatos ? "sin datos" : estatusCorto(flujo?.estatus);
    const valorEtiqueta = valorPestana(aplica, conAvance, ponderado);
    const nombreQ = `Q${q}`;
    return {
      q,
      etiqueta: [`Q${q}`, etiquetaEstatus, valorEtiqueta].filter(Boolean).join(" · "),
      punto: !aplica || !semaforo ? "is-neutro" : puntoDe(semaforo.valor),
      puntoTexto: semaforo ? semaforo.texto.toLowerCase() : "",
      titulo: `${[nombreQ, f.anio].filter((x) => x != null).join(" ")} · ${mesesDelTrimestre(q)}`,
      estatus,
      semaforo,
      nota,
      cifras,
      meses: [0, 1, 2].map((i) => this.tarjetaMes((q - 1) * 3 + i + 1, meses, aplica, autorizada)),
      pie: pieDelTrimestre(t, flujo, sinDatos),
    };
  }

  private tarjetaMes(mes: number, meses: BscKpiMes[], aplica: boolean, autorizada: boolean): TarjetaMes {
    const m = meses.find((x) => x.mes === mes);
    const estado = ESTADO_MES.find((e) => e.valor === m?.estado) ?? null;
    // Sin separador "----" todo el texto del FOR-011 quedó en actividad (§6 paso 5).
    const actividad = (m?.actividad ?? "").trim() || (m?.evidencia ? "" : (m?.textoOriginal ?? "").trim());
    const evidencia = (m?.evidencia ?? "").trim();
    const capturado = m?.id != null && (actividad !== "" || evidencia !== "");
    const fecha = m?.fechaModificacion || m?.fechaCreacion;
    let vacio = "";
    if (!capturado) {
      if (!aplica || m?.estado === "NO_APLICA") {
        vacio = "No aplica en este trimestre.";
      } else if (!autorizada) {
        vacio = "Sin captura: la planeación aún no está autorizada.";
      } else if (m?.estado === "VENCIDO") {
        vacio = "Sin captura y ya venció.";
      } else if (m?.estado === "BLOQUEADO") {
        vacio = "Sin captura; el trimestre ya se cerró.";
      } else {
        vacio = "Sin captura todavía.";
      }
    }
    return {
      mes,
      nombre: nombreMes(mes),
      estado,
      actividad,
      evidencia,
      capturo: capturado && (m?.usuarioAlta || fecha)
        ? `Capturó ${m?.usuarioAlta || SIN_DATO}${sufijoFecha(fecha)}`
        : "",
      vacio,
      capturable: false,
    };
  }

  /**
   * La pestaña que se abre: la que pidió A2 (el corte); si no, el trimestre
   * en captura o regresado; si no, el último enviado o autorizado; si no, Q1.
   */
  private pestanaInicial(f: BscKpiFicha): number {
    if (this.qPedido != null) {
      return this.qPedido;
    }
    const flujo = f.estatusTrimestres ?? [];
    const abierto = flujo.find((t) => t.estatus === "EN_CAPTURA" || t.estatus === "REGRESADO");
    if (abierto?.trimestre) {
      return abierto.trimestre;
    }
    const cerrados = flujo.filter((t) => t.estatus === "ENVIADO" || t.estatus === "AUTORIZADO")
      .map((t) => t.trimestre ?? 0);
    return cerrados.length > 0 ? Math.max(...cerrados) : 1;
  }

  /* ============================ Lateral ============================ */

  /** Lo real solo hasta el último trimestre con avance (la ficha no viene recortada). */
  private armaSeries(f: BscKpiFicha, autorizada: boolean): void {
    const k = this.kpi;
    if (!k) {
      this.serieReal = [];
      this.serieProgramado = [];
      return;
    }
    const trimestres = f.trimestres ?? [];
    const conAvance = trimestres.filter((t) => numero(t.avanceReal) != null).map((t) => t.trimestre ?? 0);
    const hasta = autorizada && conAvance.length > 0 ? Math.max(...conAvance) : 0;
    this.serieReal = TRIMESTRES.map((q) => (q <= hasta ? acumRealEn(k, q) : null));
    this.serieProgramado = TRIMESTRES.map((q) => acumProgEn(k, q));
  }

  /**
   * Los pesos y su suma sobre los trimestres que aplican (§3 «Pesos»). Si
   * cuadran lo dice el servidor (sumaPesos y avisoPesos, de
   * BscCalculadora.validarPesos con la tolerancia y exigir_suma_pesos del
   * ciclo): aquí no se repite la regla.
   */
  private armaPesos(f: BscKpiFicha): void {
    const k = this.kpi;
    if (!k) {
      this.pesos = [];
      this.textoSumaPesos = "";
      this.sumaPesosAviso = false;
      return;
    }
    this.pesos = TRIMESTRES.map((q) => ({ q, valor: aplicaEn(k, q) ? pct(pesoEn(k, q)) : "NA", na: !aplicaEn(k, q) }));
    const suma = numero(f.sumaPesos)
      ?? TRIMESTRES.filter((q) => aplicaEn(k, q)).reduce((s, q) => s + (pesoEn(k, q) ?? 0), 0);
    const inicia = k.trimestreInicio ?? 1;
    this.sumaPesosAviso = f.avisoPesos != null;
    if (this.sumaPesosAviso) {
      const desde = inicia > 1 ? `: el KPI inicia en Q${inicia}` : "";
      this.textoSumaPesos = `Suman ${pct(suma)}${desde}.`;
    } else {
      // Dentro de la tolerancia del ciclo (o el ciclo no exige la suma): se dice la suma tal cual.
      this.textoSumaPesos = `Suman ${pct(suma)} en los trimestres que aplica.`;
    }
  }

  /**
   * El tile «Mismo KPI en <año anterior>» (maqueta A3, F7.3): la L de los
   * dos años al mismo trimestre y su diferencia (trimestreComparado,
   * alcanceAnteriorMismoCorte, alcanceMismoCorte y delta: los números son
   * del servidor, aquí solo se les da formato) o por qué no se comparan
   * (estadoAnterior); debajo, la meta del año anterior, si cambió
   * (metaCambiada, la regla de T2) con su motivo, y su cierre.
   */
  private armaAnterior(f: BscKpiFicha, historial: BscHistorial[]): void {
    const k = this.kpi;
    const a = this.anterior;
    const anio = f.anio ?? 0;
    const anioAnterior = anio - 1;
    const q = f.trimestreComparado ?? null;
    const estado = f.estadoAnterior ?? null;
    this.contraCifras = [];
    this.contraNota = "";
    this.contraNotaClase = "";
    this.anteriorTexto = [];
    // T2 tiene qué enseñar si el año anterior tiene planeación del área (un KPI nuevo sale como «Nuevo»).
    this.contraEnlace = estado !== "SIN_LECTURA" && estado !== "SIN_CICLO" && estado !== "SIN_PLANEACION";
    if (!k) {
      return;
    }
    if (!a) {
      this.contraNota = `${textoSinAnterior(estado ?? "NUEVO", anioAnterior, q ?? 0)}.`;
      return;
    }

    if (q == null) {
      this.contraNota = `Sin trimestre que comparar: ${anio} todavía no tiene trimestres autorizados.`;
    } else {
      this.armaContra(f, k, a, q);
    }

    this.anteriorTexto.push(textoMetaAnterior(f, k, a, historial));
    // El servidor lo recorta al último trimestre con datos (un histórico cargado hasta Q2 no tiene cierre)
    // y solo lo manda con ese trimestre autorizado y el KPI activo: un Q4 todavía en el flujo no es el cierre.
    const alcance = numero(k.alcanceAnterior);
    const hasta = k.trimestreAlcanceAnterior ?? null;
    if (alcance != null && hasta != null && hasta !== q) {
      this.anteriorTexto.push(hasta >= 4
        ? `Cierre ${anioAnterior}: ${pct(alcance)}.`
        : `Al Q${hasta}, el último que se cargó del FOR-011 de ${anioAnterior}: ${pct(alcance)}.`);
    }
  }

  /** Los dos años al trimestre comparado, su diferencia y, debajo, hacia dónde va este año o por qué no se dice. */
  private armaContra(f: BscKpiFicha, k: BscEjercicioKpi, a: BscEjercicioKpi, q: number): void {
    const anio = f.anio ?? 0;
    const anioAnterior = anio - 1;
    const estado = f.estadoAnterior ?? null;
    const delta = numero(f.delta);
    this.contraCifras = [
      {
        etiqueta: `${anioAnterior} · Q${q}`,
        valor: estado === "NO_APLICA" ? "NA" : pct(f.alcanceAnteriorMismoCorte),
        clase: "",
      },
      { etiqueta: `${anio} · Q${q}`, valor: (k.trimestreInicio ?? 1) > q ? "NA" : pct(f.alcanceMismoCorte), clase: "" },
      { etiqueta: "Diferencia", valor: pts(delta), clase: claseDelta(delta) },
    ];
    if (delta != null) {
      this.contraNota = notaDelta(delta, anioAnterior);
      this.contraNotaClase = this.contraCifras[2].clase;
    } else {
      this.contraNota = notaSinDelta(f, k, a, q);
    }
  }

  /* ============================ Historial ============================ */

  private filaHistorial(h: BscHistorial, f: BscKpiFicha): FilaHistorial {
    const detalle: string[] = [];
    if (h.trimestre || h.mes) {
      detalle.push([h.trimestre ? `Q${h.trimestre}` : "", h.mes ? nombreMes(h.mes) : ""].filter(Boolean).join(" · "));
    }
    if (h.estatusAnterior || h.estatusNuevo) {
      detalle.push(`${textoEstatus(h.estatusAnterior)} → ${textoEstatus(h.estatusNuevo)}`);
    }
    if (h.campoModificado || h.valorAnterior || h.valorNuevo) {
      const campo = h.campoModificado ? `${h.campoModificado}: ` : "";
      detalle.push(`${campo}${h.valorAnterior || SIN_DATO} → ${h.valorNuevo || SIN_DATO}`);
    }
    if (h.comentario) {
      detalle.push(h.comentario);
    }
    return {
      fecha: fechaHora(h.fecha),
      accion: (h.accion && ACCION_HISTORIAL[h.accion]) || h.accion || SIN_DATO,
      detalle,
      usuario: h.usuario || SIN_DATO,
      otroAnio: h.idEjercicio != null && f.idEjercicio != null && h.idEjercicio !== f.idEjercicio,
    };
  }

  trackPestana(_: number, p: PestanaQ): number {
    return p.q;
  }

  trackMes(_: number, m: TarjetaMes): number {
    return m.mes;
  }
}

/** El estatus del trimestre en una palabra, para que las 4 pestañas quepan sin flechas. */
function estatusCorto(estatus: EstatusTrimestreBsc | null | undefined): string {
  switch (estatus) {
    case "AUTORIZADO":
      return "autorizado";
    case "ENVIADO":
      return "enviado";
    case "EN_CAPTURA":
      return "en captura";
    case "REGRESADO":
      return "regresado";
    case "PENDIENTE":
      return "pendiente";
    default:
      return "";
  }
}

/** El punto del semáforo de la pestaña. */
function puntoDe(semaforo: SemaforoBsc): string {
  switch (semaforo) {
    case "VERDE":
      return "is-verde";
    case "AMARILLO":
      return "is-amarillo";
    case "ROJO":
      return "is-rojo";
    default:
      return "is-neutro";
  }
}

/** Recursos, comentario, quién envió y quién resolvió el trimestre, fecha límite. */
function pieDelTrimestre(
  t: BscKpiTrimestre | undefined,
  flujo: BscEjercicioTrimestre | undefined,
  sinDatos: boolean,
): RenglonPie[] {
  const pie: RenglonPie[] = [];
  if (t?.recursos?.trim()) {
    pie.push({ etiqueta: "Recursos", valor: t.recursos.trim() });
  }
  if (t?.comentario?.trim()) {
    pie.push({ etiqueta: "Comentario", valor: t.comentario.trim() });
  }
  if (!flujo) {
    return pie;
  }
  if (flujo.fechaEnvio || flujo.usuarioEnvia) {
    pie.push({
      etiqueta: "Envió",
      valor: quienYFecha(flujo.usuarioEnvia, flujo.fechaEnvio),
    });
  }
  if (flujo.comentarioEnvio?.trim()) {
    pie.push({ etiqueta: "Comentario del envío", valor: flujo.comentarioEnvio.trim() });
  }
  const resolucion = renglonResolucion(flujo, sinDatos);
  if (resolucion) {
    pie.push(resolucion);
  }
  return pie;
}

/** Quién autorizó o regresó el trimestre o, si sigue en el flujo, su fecha límite; nulo si no toca. */
function renglonResolucion(flujo: BscEjercicioTrimestre, sinDatos: boolean): RenglonPie | null {
  if (flujo.estatus === "AUTORIZADO") {
    return {
      etiqueta: "Autorizó",
      valor: flujo.origen === "IMPORTACION"
        ? "Importado del FOR-011 (autorizado al cargar)"
        : quienYFecha(flujo.usuarioResuelve, flujo.fechaResolucion),
    };
  }
  if (flujo.estatus === "REGRESADO") {
    return {
      etiqueta: "Regresó",
      valor: quienYFecha(flujo.usuarioResuelve, flujo.fechaResolucion) +
        (flujo.motivo ? `: ${flujo.motivo}` : ""),
    };
  }
  if (flujo.estatus !== "ENVIADO" && !sinDatos) {
    return {
      etiqueta: "Fecha límite",
      valor: textoFechaLimite(flujo),
    };
  }
  return null;
}

/** "Ana López, 5 may 2026": quién y, si la hay, la fecha. */
function quienYFecha(usuario: string | null | undefined, fecha: string | null | undefined): string {
  const cuando = fecha ? `, ${fechaCorta(fecha)}` : "";
  return `${usuario || SIN_DATO}${cuando}`;
}

/** La fecha límite de envío del trimestre, con "(vencida)" si ya pasó sin enviarse. */
function textoFechaLimite(flujo: BscEjercicioTrimestre): string {
  if (!flujo.fechaLimiteEnvio) {
    return "Sin fecha límite";
  }
  return `${fechaCorta(flujo.fechaLimiteEnvio)}${flujo.sinEnviar ? " (vencida)" : ""}`;
}

/** Un estatus de trimestre o de planeación en palabras; lo desconocido tal cual. */
function textoEstatus(valor: string | null | undefined): string {
  if (!valor) {
    return SIN_DATO;
  }
  const estatus = [...ESTATUS_TRIMESTRE, ...ESTATUS_PLANEACION].find((e) => e.valor === valor);
  return estatus ? estatus.texto : valor;
}

/** La planeación sin autorizar en la frase «Planeación …: la meta todavía puede cambiar». */
function textoPlaneacion(estatus: (typeof ESTATUS_PLANEACION)[number] | undefined): string {
  if (!estatus) {
    return "sin autorizar";
  }
  return estatus.valor === "BORRADOR" ? "en borrador" : estatus.texto.toLowerCase();
}

/** NA, sin avance o sin planeación autorizada: la nota de la pestaña; vacía si tiene avance. */
function notaDelTrimestre(
  k: BscEjercicioKpi,
  q: number,
  sinDatos: boolean,
  autorizada: boolean,
  aplica: boolean,
  real: number | null,
): string {
  if (sinDatos) {
    return "Sin datos: el histórico se cargó hasta un trimestre anterior.";
  }
  if (!autorizada) {
    return "Sin avances: la planeación todavía no está autorizada.";
  }
  if (!aplica) {
    return (k.trimestreInicio ?? 1) > q
      ? `El KPI no aplica en Q${q}: inicia en Q${k.trimestreInicio}. No cuenta en el promedio del área.`
      : `El KPI no aplica en Q${q} (NA). No cuenta en el promedio del área en este trimestre.`;
  }
  if (real == null) {
    return "Sin avance real capturado todavía.";
  }
  return "";
}

/** valor_unidades es informativo ("2 de 6"): el cálculo usa I. */
function agregaUnidades(cifras: CifraQ[], k: BscEjercicioKpi, t: BscKpiTrimestre | undefined, aplica: boolean): void {
  const valorUnidades = numero(t?.valorUnidades);
  if (valorUnidades != null && aplica) {
    const de = k.unidad && k.unidad !== "%" && numero(k.metaValor) != null ? ` de ${numero(k.metaValor)}` : "";
    cifras.push({ etiqueta: "Unidades", valor: `${valorUnidades}${de}`, ayuda: `${unidad(k.unidad)}, informativo` });
  }
}

/** Lo que dice la pestaña después del estatus: NA, el ponderado o nada. */
function valorPestana(aplica: boolean, conAvance: boolean, ponderado: number | null): string {
  if (!aplica) {
    return "NA";
  }
  return conAvance ? pct(ponderado) : "";
}

/** " · 5 may 2026" para «Capturó …»; vacío sin fecha. */
function sufijoFecha(fecha: string | null | undefined): string {
  return fecha ? ` · ${fechaCorta(fecha)}` : "";
}

/** El tono de la diferencia entre los dos años: is-exito arriba, is-peligro abajo, sin tono en cero o sin dato. */
function claseDelta(delta: number | null): CifraContra["clase"] {
  if (delta == null || delta === 0) {
    return "";
  }
  return delta > 0 ? "is-exito" : "is-peligro";
}

/** Hacia dónde va este año contra el anterior, al mismo corte. */
function notaDelta(delta: number, anioAnterior: number): string {
  if (delta > 0) {
    return `Al mismo corte, este año va arriba de ${anioAnterior}.`;
  }
  return delta < 0
    ? `Al mismo corte, este año va abajo de ${anioAnterior}.`
    : `Al mismo corte, este año va igual que ${anioAnterior}.`;
}

/** Sin diferencia del servidor: por qué no se comparan los dos años. */
function notaSinDelta(f: BscKpiFicha, k: BscEjercicioKpi, a: BscEjercicioKpi, q: number): string {
  const anio = f.anio ?? 0;
  const anioAnterior = anio - 1;
  const estado = f.estadoAnterior ?? null;
  if (estado === "NO_APLICA") {
    return a.estatus === "RETIRADO"
      ? `En ${anioAnterior} el KPI estaba retirado de la planeación del área.`
      : `En ${anioAnterior} el KPI iniciaba en Q${a.trimestreInicio ?? SIN_DATO}: al Q${q} no aplicaba.`;
  }
  if (estado != null) {
    return `${textoSinAnterior(estado, anioAnterior, q, f.datosHastaAnterior)}.`;
  }
  if ((k.trimestreInicio ?? 1) > q) {
    return `Este año el KPI inicia en Q${k.trimestreInicio}: al Q${q} todavía no aplica.`;
  }
  if (f.estatusPlaneacion !== "AUTORIZADA") {
    return "La planeación de este año no está autorizada: todavía no tiene avances.";
  }
  return `${anio} no tiene alcance al Q${q} para comparar.`;
}

/** La meta del año anterior y, si cambió (metaCambiada, la regla de T2), a cuál y con qué motivo. */
function textoMetaAnterior(f: BscKpiFicha, k: BscEjercicioKpi, a: BscEjercicioKpi, historial: BscHistorial[]): string {
  const anioAnterior = (f.anio ?? 0) - 1;
  const metaAntes = meta(a.metaValor, a.metaTexto, a.unidad);
  if (k.metaCambiada === true) {
    const cambio = ultimoQue(historial, (h) => h.accion === "CAMBIO_META" && h.idEjercicio === f.idEjercicio && h.comentario);
    const metaHoy = meta(k.metaValor, k.metaTexto, k.unidad);
    const motivo = cambio ? ` (${cambio.comentario})` : "";
    return `Meta ${anioAnterior}: ${metaAntes}; este año cambió a ${metaHoy}${motivo}.`;
  }
  return `Meta ${anioAnterior}: ${metaAntes}${k.metaCambiada === false ? ", la misma de este año" : ""}.`;
}

/** El último de la lista que cumple, como filter(…).pop() sin la lista intermedia; undefined si ninguno. */
function ultimoQue<T>(lista: T[], cumple: (x: T) => unknown): T | undefined {
  for (let i = lista.length - 1; i >= 0; i--) {
    if (cumple(lista[i])) {
      return lista[i];
    }
  }
  return undefined;
}
